import React from 'react'
import { useRouter } from '@src/routes'
import { FormattedMessage } from 'react-intl'
import { Grid, Box } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'

import AddToCartButton from '@src/components/AddToCartButton'
import CatalogImage from '@src/components/CatalogImage'
import {
  useGlobalSnackbar,
  SnackbarMessages,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import { determineCatalogType } from '@src/utils/searchUtils'
import { CartItemType, CartRequestAction } from '@src/types/graphql-types'
import { usePricingAndAvailabilityQuery } from '@src/queries/PricingAndAvailabilityQuery.generated'
import { useAddToCart } from '@utils/useCart'
import EmprovePricing from './EmprovePricing'
import { OriginalError, extractData } from '@utils/errorHandler'
import { setEmproveAddToCartData } from '@utils/analytics/cartAndCheckoutEvents'
import { AddToCartPagesEnum } from '@utils/analytics/enums'
import { QUICK_CART } from '@src/queries/QuickCartQuery'
import { ALL_CARTS } from '@src/queries/AllCartsQuery'
import { useCurrentUser } from '@utils/useCurrentUser'
import { useOptlyAddToCart } from '@src/utils/analytics/OptimizelyEvents/useAddToCartEvent'

const useStyles = makeStyles((theme: Theme) => {
  return {
    documentPurchase: {
      flexWrap: 'nowrap',
    },
    imageContainer: {
      height: theme.typography.pxToRem(110),
      width: theme.typography.pxToRem(110),
      position: 'relative',
    },
    defaultImage: {
      borderRadius: theme.typography.pxToRem(8),
      maxHeight: '100%',
    },
    purchaseTerms: {
      fontSize: theme.typography.pxToRem(14),
    },
    cartDetails: {
      lineHeight: theme.typography.pxToRem(53),
      fontSize: theme.typography.pxToRem(16),
      paddingTop: theme.spacing(3),
    },
    documentDetails: {
      paddingLeft: theme.spacing(5),
      fontSize: theme.typography.pxToRem(16),
    },
    sectionHeader: {
      whiteSpace: 'nowrap',
    },
  }
})

export enum DossierSubType {
  Single = 'single',
  All = 'all',
}

const DocumentAddToCart = ({ document, handleClose }) => {
  const classes = useStyles()
  const router = useRouter()
  const { currentUser } = useCurrentUser()
  const isBlueErpIntegrationEnabled =
    !!currentUser?.metadata?.isBlueErpIntegrationEnabled
  const { catalog = '' } = router.query || {}
  const { data, loading, error } = usePricingAndAvailabilityQuery({
    variables: {
      productNumber: document.productNumber,
      brand: document.productBrand,
      quantity: 1,
      //emprove is a PDP specific feature so will use the 'catalog' param
      catalogType: determineCatalogType(catalog),
    },
  })
  const [addToCart] = useAddToCart()
  const { setSnackbar } = useGlobalSnackbar()
  const [submitting, setSubmitting] = React.useState(false)
  const sendOptlyAddToCart = useOptlyAddToCart()

  const handleAddToCart = async () => {
    setSubmitting(true)
    try {
      await addToCart({
        variables: {
          input: {
            items: [
              {
                materialNumber: document.productNumber,
                quantity: 1,
                type: CartItemType.Emprove,
                materialDescription: document.displayType,
                emproveDossierInfo: {
                  dossierId: document.assetId,
                  dossierSubType: DossierSubType.Single,
                  dossierSubLength: 1,
                  dossierMaterialNumber: document.product,
                },
              },
            ],
          },
        },
        refetchQueries: [
          !isBlueErpIntegrationEnabled
            ? {
                query: QUICK_CART,
                variables: { id: CartRequestAction.Mini },
              }
            : {
                query: ALL_CARTS,
              },
        ],
        awaitRefetchQueries: true,
      })
      handleClose()
      setSubmitting(false)
      setSnackbar(SnackbarMessages.ItemsAddedToCart, 1)
      const pricing = data?.getPricingForProduct?.materialPricing[0]
      setEmproveAddToCartData(
        [
          {
            materialNumber: document.productNumber,
            materialDescription: document.displayType,
            dossierMaterialNumber: document.product,
            pricing: pricing?.price,
            currency: pricing?.currency,
          },
        ],
        AddToCartPagesEnum.EmproveProducts
      )
      sendOptlyAddToCart(`emprove_${document?.productNumber}`)
    } catch (err) {
      setSubmitting(false)
      const { hasError } = extractData(err as OriginalError)
      let errorMessage = ''
      if (hasError('CANNOT_CONTAIN_OTHER_PRODUCT_TYPE_WITH_EMPROVE_PRODUCTS')) {
        errorMessage = SnackbarMessages.CartMixingError
      } else if (
        hasError('CANNOT_CONTAIN_SAME_DOSSIER_WITH_EMPROVE_PRODUCTS')
      ) {
        errorMessage = SnackbarMessages.SameEmproveError
      } else {
        errorMessage = SnackbarMessages.GenericError
      }
      setSnackbar(errorMessage)
    }
  }

  return (
    <>
      <Grid container className={classes.documentPurchase}>
        <Grid item className={classes.imageContainer}>
          <CatalogImage image={{}} className={classes.defaultImage} />
        </Grid>
        <Grid item className={classes.documentDetails}>
          <Grid container direction="column">
            <Grid item>
              <Box
                fontWeight="fontWeightMedium"
                pb={2}
                className={classes.sectionHeader}
              >
                <FormattedMessage
                  id="PURCHASE_SINGLE_DOSSIER"
                  defaultMessage="Purchase Single Dossier"
                />
              </Box>
            </Grid>
            <Grid item className={classes.purchaseTerms}>
              <FormattedMessage
                id="DOSSIER_PURCHASE_TERMS"
                defaultMessage="Includes dossier updates for two years"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid className={classes.cartDetails}>
        <Box fontWeight="fontWeightMedium">{document.displayType}</Box>
        <Box>
          <EmprovePricing data={data} loading={loading} error={error} />
        </Box>
        <Box>
          <AddToCartButton
            handleAddToCart={handleAddToCart}
            dirty
            isSubmitting={submitting}
            canAddToCart
            hideAddToCartForPrepackItems={false}
            disabled={error ? true : false}
          />
        </Box>
      </Grid>
    </>
  )
}

export default DocumentAddToCart
